import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const [lockOpen, setLockOpen] = useState(false);
  const [lockMessage, setLockMessage] = useState("");
  const navigate = useNavigate();

  const showLock = useCallback((msg) => {
    setLockMessage(msg || "Your free trial has ended. Subscribe to keep learning.");
    setLockOpen(true);
  }, []);

  const closeLock = useCallback(() => setLockOpen(false), []);

  useEffect(() => {
    const id = api.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 402) {
          const detail = err.response.data?.detail;
          const raw = err.response.data?._detailRaw;
          const msg =
            (raw && raw.message) ||
            (typeof detail === "string" ? detail : null) ||
            "Your free trial has ended. Subscribe to keep learning.";
          showLock(msg);
        }
        return Promise.reject(err);
      },
    );
    return () => api.interceptors.response.eject(id);
  }, [showLock]);

  const goPricing = useCallback(() => {
    setLockOpen(false);
    navigate("/pricing");
  }, [navigate]);

  const value = useMemo(
    () => ({ lockOpen, lockMessage, showLock, closeLock, goPricing }),
    [lockOpen, lockMessage, showLock, closeLock, goPricing],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      {lockOpen && (
        <LockedModal message={lockMessage} onClose={closeLock} onUpgrade={goPricing} />
      )}
    </SubscriptionContext.Provider>
  );
}

function LockedModal({ message, onClose, onUpgrade }) {
  return (
    <div
      data-testid="locked-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-card border border-border shadow-2xl p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-16 h-16 rounded-2xl bg-[hsl(var(--brimi-coral))] text-white flex items-center justify-center text-3xl shadow-lg">
          ✨
        </div>
        <div className="text-xs font-black uppercase tracking-widest text-[hsl(var(--brimi-coral))] mt-4 mb-2 text-center">
          Trial ended
        </div>
        <h2 className="text-2xl font-black text-center mb-2">
          Keep your momentum going
        </h2>
        <p className="text-muted-foreground text-center mb-6">{message}</p>
        <div className="grid gap-2">
          <button
            data-testid="locked-upgrade-btn"
            onClick={onUpgrade}
            className="pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white py-3 w-full"
          >
            See plans &amp; upgrade
          </button>
          <button
            data-testid="locked-close-btn"
            onClick={onClose}
            className="pill-btn bg-transparent hover:bg-foreground/5 text-muted-foreground py-2 w-full text-sm"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

export const useSubscription = () => useContext(SubscriptionContext);
